import React, { useEffect, useMemo, useState } from "react";
import { Search, IndianRupee, Calendar, Receipt, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const STATUS_COPY = {
  trial: "Trial",
  trial_expired: "Trial Ended",
  active: "Active",
  grace: "In Grace",
  subscription_expired: "Sub Expired",
  suspended: "Suspended",
  archived: "Archived",
};

const STATUS_CLASS = {
  active: "bg-green-50 text-green-800 border-green-200",
  trial: "bg-blue-50 text-blue-800 border-blue-200",
  grace: "bg-amber-50 text-amber-900 border-amber-200",
  trial_expired: "bg-red-50 text-red-800 border-red-200",
  subscription_expired: "bg-red-50 text-red-800 border-red-200",
  suspended: "bg-stone-100 text-stone-700 border-stone-300",
  archived: "bg-stone-100 text-stone-700 border-stone-300",
};

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

export default function SuperAdminSubscriptions() {
  const [data, setData] = useState({ tenants: [], aggregates: {} });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [dialog, setDialog] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/superadmin/subscriptions/overview");
      setData(data);
    } catch {
      toast.error("Failed to load subscriptions");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const tenants = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (data.tenants || []).filter((t) => {
      if (filter !== "all" && t.status !== filter) return false;
      if (!needle) return true;
      return (
        (t.name || "").toLowerCase().includes(needle) ||
        (t.slug || "").toLowerCase().includes(needle)
      );
    });
  }, [data.tenants, q, filter]);

  return (
    <div data-testid="super-subscriptions">
      <div className="overline mb-2">Platform</div>
      <h1 className="font-display text-4xl mb-6">Subscriptions</h1>

      <RevenueCards a={data.aggregates || {}} />

      <div className="flex flex-wrap items-center gap-3 mb-3 mt-8">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--kb-muted))]" />
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by brand or slug…"
            className="kb-input !pl-8"
            data-testid="sub-search"
          />
        </div>
        <select
          value={filter} onChange={(e) => setFilter(e.target.value)}
          className="kb-input !py-2 !w-auto"
          data-testid="sub-filter"
        >
          <option value="all">All statuses</option>
          {Object.entries(STATUS_COPY).map(([k, v]) => (<option key={k} value={k}>{v}</option>))}
        </select>
        <button onClick={load} className="kb-btn-outline !py-2 !px-3 !text-xs inline-flex items-center gap-1.5" data-testid="sub-refresh">
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      <div className="bg-white border border-[hsl(var(--kb-border))] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[hsl(var(--kb-surface-alt))] text-left">
            <tr>
              <th className="px-4 py-3 overline">Tenant</th>
              <th className="px-4 py-3 overline">Status</th>
              <th className="px-4 py-3 overline">Plan</th>
              <th className="px-4 py-3 overline">Expiry</th>
              <th className="px-4 py-3 overline text-right">Outstanding</th>
              <th className="px-4 py-3 overline text-right">Total Paid</th>
              <th className="px-4 py-3 overline">Last Payment</th>
              <th className="px-4 py-3 overline">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan={8} className="px-4 py-10 text-center text-[hsl(var(--kb-muted))]">Loading…</td></tr>)}
            {!loading && tenants.length === 0 && (<tr><td colSpan={8} className="px-4 py-10 text-center text-[hsl(var(--kb-muted))]">No tenants</td></tr>)}
            {tenants.map((t) => (
              <tr key={t.id} className="border-t border-[hsl(var(--kb-border))]" data-testid={`sub-row-${t.slug}`}>
                <td className="px-4 py-3">
                  <div className="font-medium">{t.name || t.slug}</div>
                  <div className="text-xs text-[hsl(var(--kb-muted))] font-mono">{t.slug}</div>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-1 border ${STATUS_CLASS[t.status] || "bg-stone-50 border-stone-200"}`}>
                    {STATUS_COPY[t.status] || t.status}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {t.plan_slug ? (
                    <>
                      <div className="text-xs font-medium">{t.plan_slug}</div>
                      <div className="text-[11px] text-[hsl(var(--kb-muted))]">{t.billing_cycle} · {inr(t.price_at_purchase)}</div>
                    </>
                  ) : "—"}
                </td>
                <td className="px-4 py-3 text-xs">
                  {t.subscription_active_until ? (
                    <>
                      <div>{new Date(t.subscription_active_until).toLocaleDateString("en-IN")}</div>
                      <div className={`text-[11px] ${
                        (t.days_remaining ?? 0) < 0 ? "text-red-700" :
                        (t.days_remaining ?? 0) <= 7 ? "text-amber-700" :
                        "text-[hsl(var(--kb-muted))]"
                      }`}>
                        {(t.days_remaining ?? 0) >= 0 ? `${t.days_remaining}d left` : `${Math.abs(t.days_remaining)}d overdue`}
                      </div>
                    </>
                  ) : t.trial_ends_at ? (
                    <div className="text-[11px] text-[hsl(var(--kb-muted))]">Trial: {new Date(t.trial_ends_at).toLocaleDateString("en-IN")}</div>
                  ) : "—"}
                </td>
                <td className="px-4 py-3 text-right font-mono text-sm">
                  {t.outstanding > 0 ? <span className="text-red-700">{inr(t.outstanding)}</span> : <span className="text-[hsl(var(--kb-muted))]">—</span>}
                </td>
                <td className="px-4 py-3 text-right font-mono text-sm">{inr(t.total_paid_lifetime)}</td>
                <td className="px-4 py-3 text-xs text-[hsl(var(--kb-muted))]">
                  {t.last_payment_at ? new Date(t.last_payment_at).toLocaleDateString("en-IN") : "—"}
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-col gap-1">
                    <button
                      onClick={() => setDialog({ mode: "payment", tenant: t })}
                      className="text-xs underline hover:text-[hsl(var(--kb-primary))]"
                      data-testid={`sub-pay-${t.slug}`}
                    >
                      Record Payment
                    </button>
                    <button
                      onClick={() => setDialog({ mode: "extend", tenant: t })}
                      className="text-xs underline hover:text-[hsl(var(--kb-primary))]"
                      data-testid={`sub-extend-${t.slug}`}
                    >
                      Extend Expiry
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {dialog?.mode === "payment" && (
        <RecordPaymentDialog tenant={dialog.tenant} onClose={() => setDialog(null)} onDone={load} />
      )}
      {dialog?.mode === "extend" && (
        <ExtendDialog tenant={dialog.tenant} onClose={() => setDialog(null)} onDone={load} />
      )}
    </div>
  );
}

function RevenueCards({ a }) {
  const cards = [
    { label: "MRR", value: inr(a.mrr || 0), icon: IndianRupee, hint: "Monthly recurring revenue" },
    { label: "ARR", value: inr(a.arr || 0), icon: IndianRupee, hint: "Annualised" },
    { label: "Revenue · 30d", value: inr(a.revenue_last_30_days || 0), icon: Receipt },
    { label: "Revenue · 12m", value: inr(a.revenue_last_365_days || 0), icon: Receipt },
  ];
  const sc = a.status_counts || {};
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-2">
      {cards.map((c) => {
        const Icon = c.icon;
        return (
          <div key={c.label} className="bg-white border border-[hsl(var(--kb-border))] p-4">
            <div className="flex items-center justify-between">
              <div className="overline">{c.label}</div>
              <Icon size={14} className="text-[hsl(var(--kb-muted))]" />
            </div>
            <div className="font-display text-3xl mt-1">{c.value}</div>
            {c.hint && <div className="text-[11px] text-[hsl(var(--kb-muted))] mt-1">{c.hint}</div>}
          </div>
        );
      })}
      <div className="bg-white border border-[hsl(var(--kb-border))] p-4 md:col-span-4">
        <div className="overline mb-2">By status ({a.tenant_count || 0} total)</div>
        <div className="flex flex-wrap gap-3 text-xs">
          {Object.entries(sc).map(([k, v]) => (
            <span key={k} className={`px-2.5 py-1 border ${STATUS_CLASS[k] || "bg-stone-50 border-stone-200"}`}>
              <b>{v}</b> {STATUS_COPY[k] || k}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function ModalShell({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-[hsl(var(--kb-border))] max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="font-display text-2xl mb-4">{title}</div>
        {children}
      </div>
    </div>
  );
}

function RecordPaymentDialog({ tenant, onClose, onDone }) {
  const [amount, setAmount] = useState(tenant.outstanding > 0 ? tenant.outstanding : "");
  const [method, setMethod] = useState("upi");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [activateNow, setActivateNow] = useState(false);
  const [activeUntil, setActiveUntil] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!amount || Number(amount) <= 0) return toast.error("Enter a valid amount");
    setSaving(true);
    try {
      await api.post(`/superadmin/tenants/${tenant.id}/receipts`, {
        amount: Number(amount),
        method,
        invoice_id: tenant.last_invoice?.id,
        reference: reference || null,
        notes: notes || null,
        activate_now: activateNow,
        active_until: activateNow && activeUntil ? new Date(activeUntil).toISOString() : null,
      });
      toast.success(`Recorded ${inr(amount)} from ${tenant.name}`);
      onDone();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to record");
    } finally { setSaving(false); }
  };

  return (
    <ModalShell title={`Record payment · ${tenant.name || tenant.slug}`} onClose={onClose}>
      <div className="space-y-3 text-sm">
        {tenant.outstanding > 0 && (
          <div className="bg-amber-50 border border-amber-200 px-3 py-2 text-xs">
            Outstanding: <b>{inr(tenant.outstanding)}</b>
          </div>
        )}
        <div>
          <label className="overline block mb-1">Amount (₹)</label>
          <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="kb-input" data-testid="pay-amount" autoFocus />
        </div>
        <div>
          <label className="overline block mb-1">Method</label>
          <select value={method} onChange={(e) => setMethod(e.target.value)} className="kb-input" data-testid="pay-method">
            <option value="upi">UPI</option>
            <option value="cash">Cash</option>
            <option value="bank_transfer">Bank transfer</option>
            <option value="razorpay_manual">Razorpay (manual)</option>
            <option value="cheque">Cheque</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div>
          <label className="overline block mb-1">Reference (optional)</label>
          <input value={reference} onChange={(e) => setReference(e.target.value)} className="kb-input" placeholder="UTR / txn id / cheque #" />
        </div>
        <div>
          <label className="overline block mb-1">Notes (optional)</label>
          <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="kb-input" />
        </div>
        <label className="flex items-start gap-2 text-xs cursor-pointer pt-2 border-t border-[hsl(var(--kb-border))]">
          <input type="checkbox" checked={activateNow} onChange={(e) => setActivateNow(e.target.checked)} data-testid="pay-activate" />
          <span>
            Activate tenant now (use for partial payments — sets a custom expiry instead of waiting for full payment)
          </span>
        </label>
        {activateNow && (
          <div>
            <label className="overline block mb-1">Active until</label>
            <input type="date" value={activeUntil} onChange={(e) => setActiveUntil(e.target.value)} className="kb-input" data-testid="pay-until" />
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="kb-btn-outline !py-2 !px-4 !text-xs">Cancel</button>
          <button onClick={submit} disabled={saving} className="kb-btn-primary !py-2 !px-4 !text-xs" data-testid="pay-submit">
            {saving ? "Saving…" : "Record payment"}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

function ExtendDialog({ tenant, onClose, onDone }) {
  const [until, setUntil] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!until) return toast.error("Pick an end date");
    setSaving(true);
    try {
      await api.post(`/superadmin/tenants/${tenant.id}/subscription/extend`, {
        active_until: new Date(until).toISOString(),
        reason: reason || null,
      });
      toast.success(`Extended ${tenant.name} to ${until}`);
      onDone();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to extend");
    } finally { setSaving(false); }
  };

  return (
    <ModalShell title={`Extend subscription · ${tenant.name || tenant.slug}`} onClose={onClose}>
      <div className="space-y-3 text-sm">
        {tenant.subscription_active_until && (
          <div className="bg-stone-50 border border-stone-200 px-3 py-2 text-xs">
            Currently: <b>{new Date(tenant.subscription_active_until).toLocaleDateString("en-IN")}</b>
          </div>
        )}
        <div>
          <label className="overline block mb-1 flex items-center gap-1"><Calendar size={11} /> New active until</label>
          <input type="date" value={until} onChange={(e) => setUntil(e.target.value)} className="kb-input" data-testid="ext-until" autoFocus />
        </div>
        <div>
          <label className="overline block mb-1">Reason (optional)</label>
          <input value={reason} onChange={(e) => setReason(e.target.value)} className="kb-input" placeholder="Goodwill / loyalty / etc." />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="kb-btn-outline !py-2 !px-4 !text-xs">Cancel</button>
          <button onClick={submit} disabled={saving} className="kb-btn-primary !py-2 !px-4 !text-xs" data-testid="ext-submit">
            {saving ? "Saving…" : "Extend"}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}
